import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSnippet } from '../../contexts/SnippetContext';
import Logo from '../Logo';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { hasVerifiedSnippet, initializeSnippets } = useSnippet();
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize snippets when user enters dashboard
  useEffect(() => {
    if (user) {
      initializeSnippets();
    }
  }, [user]); // Removed initializeSnippets from dependency array to prevent infinite loop

  const handleLogout = async () => {
    await logout();
    // Small delay to ensure logout completes
    setTimeout(() => {
      navigate('/login');
    }, 100);
  };

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: 'fas fa-tachometer-alt' },
    { name: 'Website Management', href: '/dashboard/snippets', icon: 'fas fa-globe' },
    { name: 'Pages', href: '/dashboard/pages', icon: 'fas fa-file-alt', requiresVerification: true },
    { name: 'AI Traffic Analytics', href: '/dashboard/analytics', icon: 'fas fa-chart-line', requiresVerification: true },
    { name: 'Sentiment', href: '/dashboard/sentiment', icon: 'fas fa-smile', requiresVerification: true },
    { name: 'Citations', href: '/dashboard/citations', icon: 'fas fa-quote-right', requiresVerification: true },
    { name: 'Prompts', href: '/dashboard/prompts', icon: 'fas fa-comments', requiresVerification: true },
    { name: 'Action Center', href: '/dashboard/actions', icon: 'fas fa-cogs', badge: 'New', requiresVerification: true },
  ];

  const researchNavigation = [
    { name: 'Prompt Explorer', href: '/dashboard/explorer', icon: 'fas fa-search', badge: 'New', requiresVerification: true },
  ];

  const configureNavigation = [
    { name: 'Topics and Prompts', href: '/dashboard/topics', icon: 'fas fa-tags', requiresVerification: true },
    { name: 'Competitors', href: '/dashboard/competitors', icon: 'fas fa-users', requiresVerification: true },
  ];

  return (
    <div className="d-flex">
      {/* Sidebar */}
      <div className={`sidebar bg-dark text-white ${sidebarOpen ? 'show' : ''}`} style={{width: '280px', minHeight: '100vh'}}>
        <div className="p-3 border-bottom border-secondary">
          <div className="d-flex align-items-center">
            <Logo size="sm" className="me-2" />
            <span className="fw-bold fs-5">Dream Digi</span>
          </div>
        </div>



        {/* Navigation */}
        <nav className="p-3">
          <div className="mb-4">
            {navigation.map((item) => {
              const isDisabled = item.requiresVerification && !hasVerifiedSnippet;
              return (
                <div key={item.name}>
                  {isDisabled ? (
                    <div
                      className="d-flex align-items-center p-2 rounded text-decoration-none mb-1 text-muted"
                      style={{ cursor: 'not-allowed', opacity: 0.5 }}
                      title="Please verify a website first"
                    >
                      <i className={`${item.icon} me-3`} style={{width: '20px'}}></i>
                      <span className="flex-grow-1">{item.name}</span>
                      <i className="fas fa-lock ms-2" title="Requires website verification"></i>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`d-flex align-items-center p-2 rounded text-decoration-none mb-1 ${
                        location.pathname === item.href
                          ? 'bg-primary text-white'
                          : 'text-white-50 hover-bg-secondary'
                      }`}
                    >
                      <i className={`${item.icon} me-3`} style={{width: '20px'}}></i>
                      <span className="flex-grow-1">{item.name}</span>
                      {item.badge && (
                        <span className="badge bg-success ms-auto">New</span>
                      )}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mb-4">
            <h6 className="text-uppercase text-muted small fw-bold mb-2">Research</h6>
            {researchNavigation.map((item) => {
              const isDisabled = item.requiresVerification && !hasVerifiedSnippet;
              return (
                <div key={item.name}>
                  {isDisabled ? (
                    <div
                      className="d-flex align-items-center p-2 rounded text-decoration-none mb-1 text-muted"
                      style={{ cursor: 'not-allowed', opacity: 0.5 }}
                      title="Please verify a website first"
                    >
                      <i className={`${item.icon} me-3`} style={{width: '20px'}}></i>
                      <span className="flex-grow-1">{item.name}</span>
                      <i className="fas fa-lock ms-2" title="Requires website verification"></i>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`d-flex align-items-center p-2 rounded text-decoration-none mb-1 ${
                        location.pathname === item.href
                          ? 'bg-primary text-white'
                          : 'text-white-50 hover-bg-secondary'
                      }`}
                    >
                      <i className={`${item.icon} me-3`} style={{width: '20px'}}></i>
                      <span className="flex-grow-1">{item.name}</span>
                      {item.badge && (
                        <span className="badge bg-success ms-auto">New</span>
                      )}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          <div>
            <h6 className="text-uppercase text-muted small fw-bold mb-2">Configure</h6>
            {configureNavigation.map((item) => {
              const isDisabled = item.requiresVerification && !hasVerifiedSnippet;
              return (
                <div key={item.name}>
                  {isDisabled ? (
                    <div
                      className="d-flex align-items-center p-2 rounded text-decoration-none mb-1 text-muted"
                      style={{ cursor: 'not-allowed', opacity: 0.5 }}
                      title="Please verify a website first"
                    >
                      <i className={`${item.icon} me-3`} style={{width: '20px'}}></i>
                      <span className="flex-grow-1">{item.name}</span>
                      <i className="fas fa-lock ms-2" title="Requires website verification"></i>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`d-flex align-items-center p-2 rounded text-decoration-none mb-1 ${
                        location.pathname === item.href
                          ? 'bg-primary text-white'
                          : 'text-white-50 hover-bg-secondary'
                      }`}
                    >
                      <i className={`${item.icon} me-3`} style={{width: '20px'}}></i>
                      <span className="flex-grow-1">{item.name}</span>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1">
        {/* Top Navigation */}
        <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom">
          <div className="container-fluid">
            <button
              className="btn btn-link d-lg-none"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <i className="fas fa-bars"></i>
            </button>

            <div className="navbar-nav ms-auto">
              <div className="nav-item dropdown">
                <a className="nav-link dropdown-toggle d-flex align-items-center" href="#" role="button" data-bs-toggle="dropdown">
                  <div className="bg-primary rounded-circle me-2 d-flex align-items-center justify-content-center" style={{width: '32px', height: '32px'}}>
                    <span className="text-white fw-bold small">
                      {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="d-none d-md-block">{user?.firstName || user?.email}</span>
                </a>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li><Link className="dropdown-item" to="/dashboard/profile"><i className="fas fa-user me-2"></i>Profile</Link></li>
                  <li><a className="dropdown-item" href="#"><i className="fas fa-cog me-2"></i>Settings</a></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><a className="dropdown-item" href="#" onClick={handleLogout}><i className="fas fa-sign-out-alt me-2"></i>Logout</a></li>
                </ul>
              </div>
            </div>
          </div>
        </nav>

        {/* Page Content */}
        <div className="p-4 bg-light min-vh-100">
          {children}
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-lg-none"
          style={{zIndex: 1040}}
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout;
